import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import crmService from "../../crmConfig.json";
import { crmAxios, getCrmAuth, setCrmAuth } from "../../app/crmApi";
import { errorToast, successToast } from "../../helpers/toaster";

const initialState = {
  auth: getCrmAuth(),
  loading: false,
  registerStatus: false,
  forgotStatus: false,
  resetStatus: false,
  verifyStatus: "",
};

export const crmLoginAsync = createAsyncThunk(
  "crmAuth/login",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const res = await crmAxios.post(crmService.login, { email, password });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

export const crmRegisterAsync = createAsyncThunk(
  "crmAuth/register",
  async (formData, { rejectWithValue }) => {
    try {
      const res = await crmAxios.post(crmService.register, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

export const crmForgotPasswordAsync = createAsyncThunk(
  "crmAuth/forgotPassword",
  async (email, { rejectWithValue }) => {
    try {
      const res = await crmAxios.post(crmService.forgotPassword, { email });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

export const crmResetPasswordAsync = createAsyncThunk(
  "crmAuth/resetPassword",
  async ({ token, password }, { rejectWithValue }) => {
    try {
      const res = await crmAxios.post(`${crmService.resetPassword}/${token}`, {
        password,
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

export const crmVerifyEmailAsync = createAsyncThunk(
  "crmAuth/verifyEmail",
  async (token, { rejectWithValue }) => {
    try {
      const res = await crmAxios.get(`${crmService.verifyEmail}/${token}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

export const crmChangePasswordAsync = createAsyncThunk(
  "crmAuth/changePassword",
  async ({ currentPassword, newPassword }, { rejectWithValue }) => {
    try {
      const res = await crmAxios.post(crmService.changePassword, {
        currentPassword,
        newPassword,
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

export const crmLogoutAsync = createAsyncThunk("crmAuth/logout", async () => {
  try {
    await crmAxios.post(crmService.logout);
  } catch (e) {
    // ignore - we clear local state regardless
  }
});

const crmAuthSlice = createSlice({
  name: "crmAuth",
  initialState,
  reducers: {
    crmLogout: (state) => {
      state.auth = null;
      setCrmAuth(null);
    },
    resetCrmAuthFlags: (state) => {
      state.registerStatus = false;
      state.forgotStatus = false;
      state.resetStatus = false;
      state.verifyStatus = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(crmLoginAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(crmLoginAsync.fulfilled, (state, { payload }) => {
        state.loading = false;
        if (payload?.success) {
          state.auth = payload.data;
          setCrmAuth(payload.data);
          successToast(payload.message || "Login successful");
        } else {
          errorToast(payload?.message || "Login failed");
        }
      })
      .addCase(crmLoginAsync.rejected, (state, { payload }) => {
        state.loading = false;
        errorToast(payload?.message || "Login failed");
      })
      .addCase(crmRegisterAsync.pending, (state) => {
        state.loading = true;
        state.registerStatus = false;
      })
      .addCase(crmRegisterAsync.fulfilled, (state, { payload }) => {
        state.loading = false;
        if (payload?.success) {
          state.registerStatus = true;
          successToast(payload.message);
        } else {
          errorToast(payload?.message || "Registration failed");
        }
      })
      .addCase(crmRegisterAsync.rejected, (state, { payload }) => {
        state.loading = false;
        errorToast(payload?.message || "Registration failed");
      })
      .addCase(crmForgotPasswordAsync.pending, (state) => {
        state.loading = true;
        state.forgotStatus = false;
      })
      .addCase(crmForgotPasswordAsync.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.forgotStatus = true;
        successToast(payload?.message);
      })
      .addCase(crmForgotPasswordAsync.rejected, (state, { payload }) => {
        state.loading = false;
        errorToast(payload?.message || "Something went wrong");
      })
      .addCase(crmResetPasswordAsync.pending, (state) => {
        state.loading = true;
        state.resetStatus = false;
      })
      .addCase(crmResetPasswordAsync.fulfilled, (state, { payload }) => {
        state.loading = false;
        if (payload?.success) {
          state.resetStatus = true;
          successToast(payload.message);
        } else {
          errorToast(payload?.message || "Reset failed");
        }
      })
      .addCase(crmResetPasswordAsync.rejected, (state, { payload }) => {
        state.loading = false;
        errorToast(payload?.message || "Reset failed");
      })
      .addCase(crmVerifyEmailAsync.pending, (state) => {
        state.verifyStatus = "pending";
      })
      .addCase(crmVerifyEmailAsync.fulfilled, (state, { payload }) => {
        state.verifyStatus = payload?.success ? "success" : "failed";
      })
      .addCase(crmVerifyEmailAsync.rejected, (state) => {
        state.verifyStatus = "failed";
      })
      .addCase(crmChangePasswordAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(crmChangePasswordAsync.fulfilled, (state, { payload }) => {
        state.loading = false;
        if (payload?.success) {
          successToast(payload.message);
        } else {
          errorToast(payload?.message || "Failed to change password");
        }
      })
      .addCase(crmChangePasswordAsync.rejected, (state, { payload }) => {
        state.loading = false;
        errorToast(payload?.message || "Failed to change password");
      })
      .addCase(crmLogoutAsync.fulfilled, (state) => {
        state.auth = null;
        setCrmAuth(null);
      });
  },
});

export const { crmLogout, resetCrmAuthFlags } = crmAuthSlice.actions;

export const selectCrmAuth = (state) => state.crmAuth.auth;
export const selectCrmLoading = (state) => state.crmAuth.loading;
export const selectCrmRegisterStatus = (state) => state.crmAuth.registerStatus;
export const selectCrmForgotStatus = (state) => state.crmAuth.forgotStatus;
export const selectCrmResetStatus = (state) => state.crmAuth.resetStatus;
export const selectCrmVerifyStatus = (state) => state.crmAuth.verifyStatus;

export default crmAuthSlice.reducer;
